import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { devicesTableQuery, usersPeriodTableQuery, usersTableQuery, type Req, type Row } from '../api/productivitee'
import { today, type Period } from '../api/securitee'
import { Content } from '../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { PeriodWithPresets } from './period'
import { deviceColumns, userColumns, type Column } from './columns'
import { DeviceTabs, UserPeriodTabs, UserTabs } from './Tabs'

/**
 * Productivitee - `device_productivity` (#allDevices, getDevicesData, cu ziua
 * din antet `DevicesFilters.selectedDate`), `user_productivity` (getUsersData,
 * pe zi) si `user_productivity_period` (getUsersPeriodData, from-to).
 *
 * Tabloul modulului si-a luat fisierul lui, `DashboardPage.tsx`: de cand are
 * cele trei grafice inapoi si sta pe desenul de carduri, n-are nimic in comun
 * cu cele trei liste de aici.
 */

type TableQuery = ReturnType<typeof devicesTableQuery>

function ListPage<F>({ id, columns, filter, initial, header, query, defaultOrder, details }: {
  id: string
  columns: Column[]
  filter: F
  initial?: F
  header(filter: F, onChange: (f: F) => void): ReactNode
  query(r: Req, filter: F): TableQuery
  defaultOrder: number
  details(row: Row, filter: F): ReactNode
}) {
  const [f, setF] = useState<F>(() => initial ?? filter)
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: defaultOrder, dir: 'desc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[defaultOrder]!
  const opts = query({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search }, f)
  const q = useQuery({ ...opts, placeholderData: keepPreviousData })
  return (
    <Content>
      <div id="content">
        <DataTable
          id={id}
          columns={columns as TableColumn[]}
          rows={q.data?.data ?? []}
          total={q.data?.recordsTotal ?? 0}
          fetching={q.isFetching}
          error={q.isError ? (q.error as Error).message : null}
          onRetry={() => void q.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          /* Cand lista iese goala, capatul spune si de ce - vezi `emptyHint`
             din api/productivitee.ts. Fara el, ramane "Nothing here yet.". */
          emptyHint={q.data?.emptyHint}
          headerButtons={header(f, setF)}
          /* `DataTablesProperties.buttons` din general.js: Copy, Columns (colvis)
             si Refresh (`dt.ajax.reload(null, false)`). Cele trei liste din
             Productivitee erau singurele din v4 fara ele. */
          footerButtons={{ copy: true, colvis: true, refresh: () => void q.refetch() }}
          details={(row) => details(row, f)}
        />
      </div>
    </Content>
  )
}

/**
 * ZIUA LISTELOR: `<input type="date">`, CA IN RESTUL APLICATIEI
 * -----------------------------------------------------------------------------
 *
 * Reclamatia (Roxana, 24 septembrie 2026): casuta zilei din antetul portocaliu
 * „e prea lata si n-are iconita in interior", pe cand in restul portalului
 * campul de data e ingust, scrie `mm / dd / yyyy` si are calendarul browserului
 * in dreapta, INAUNTRU.
 *
 * MASURAT (Edge headless, foile adevarate din `public/css`, fereastra
 * 1600x900): `DatePicker` in `li.tk-header-tools` iesea 204x34 - mai lat decat
 * casuta de cautare de langa (200px) - iar `<i class="fa fa-calendar-o ico">`
 * avea `:before` cu `content: none`. Fontul ERA incarcat
 * (`document.fonts.check('900 14px "Font Awesome 5 Free"') === true`) si
 * `app.css` ERA pe pagina - sta in `PORTAL_STYLES` din `vite.config.ts`, adica
 * pe TOATE ecranele. Numai numele clasei era al lui Font Awesome 4:
 * `fa-calendar-o` nu exista in Font Awesome 5, unde iconita se cheama
 * `far fa-calendar`. De-aia se desena un patrat gol de 34x34, si de-aia cele
 * doua carpeli de pe 23 septembrie (stiluri inline pe iconita, apoi scoaterea
 * celei dublate) n-au schimbat nimic pe ecran: asezau corect un glif care nu
 * exista.
 *
 * Casuta nativa e cea din restul aplicatiei - peste treizeci de locuri, printre
 * ele `inventoree/EndpointPerformancePage`, `ui/EventsChrome` si chiar
 * `productivitee/DayReportPage` de alaturi: 131x30 masurati la fel, cu
 * calendarul desenat de browser inauntru. Nu atarna nici de `app.css`, nici de
 * Font Awesome, deci nu se mai poate strica dintr-o foaie.
 *
 * CONTRACTUL CATRE SERVER NU SE SCHIMBA: `value` a fost si ramane `yyyy-mm-dd`
 * - fix ce citeste si ce scrie `<input type="date">`. Singura purtare noua ar
 * fi GOLIREA campului, pe care casuta veche (`readOnly`) n-o ingaduia; o
 * schimbare in gol se lasa deoparte, ca lista sa nu ceara o zi vida.
 */
export function DayFilter({ date, onChange }: { date: string; onChange(d: string): void }) {
  return (
    <div className="daterange tk-header-extra" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input
        className="form-control input-sm"
        type="date"
        id="filterDate"
        name="selectedDate"
        aria-label="Day"
        /* `form-control` e `display: block; width: 100%` in Bootstrap 3, iar
           intr-un rand flex asta intinde casuta cat tine bara - aceeasi
           masuratoare ca la Endpoint Performance. */
        style={{ width: 'auto' }}
        value={date}
        onChange={(e) => {
          if (e.target.value !== '') onChange(e.target.value)
        }}
      />
    </div>
  )
}

/* Selectorul de perioada al modulului sta acum in `period.tsx`: il cer si fila
   Evolution, si tabloul de conducere, iar fisierul asta incarca `Tabs.tsx` -
   lasat aici, ar fi facut un cerc de module. Se da mai departe de aici, ca
   importurile si probele scrise pana acum sa ramana valabile. */
export { PeriodWithPresets } from './period'

export function ProductiviteeDevicesPage() {
  return (
    <ListPage<string>
      id="allDevices"
      columns={deviceColumns()}
      filter={today()}
      header={(date, onChange) => <DayFilter date={date} onChange={onChange} />}
      query={(r, date) => devicesTableQuery({ ...r, date })}
      defaultOrder={2}
      details={(row, date) => <DeviceTabs id={Number(row.device_id)} date={date} tip={Number(row.Tip)} />}
    />
  )
}

export function ProductiviteeUsersPage() {
  return (
    <ListPage<string>
      id="allUsers"
      columns={userColumns(false)}
      filter={today()}
      header={(date, onChange) => <DayFilter date={date} onChange={onChange} />}
      query={(r, date) => usersTableQuery({ ...r, date })}
      defaultOrder={2}
      details={(row, date) => <UserTabs id={Number(row.ID)} date={date} />}
    />
  )
}

export function ProductiviteeUsersPeriodPage() {
  return (
    <ListPage<Period>
      id="allUsersPeriod"
      columns={userColumns(true)}
      filter={{ from: today(), to: today() }}
      header={(period, onChange) => <PeriodWithPresets period={period} onChange={onChange} />}
      query={(r, period) => usersPeriodTableQuery({ ...r, ...period })}
      defaultOrder={2}
      details={(row, period) => <UserPeriodTabs id={Number(row.ID)} from={String(row.working_day ?? period.from).slice(0, 10)} period={period} />}
    />
  )
}
